// Shared helper for surfacing errors that fire BEFORE a conversation exists
// — i.e. anywhere the ErrorLogBubble can't attach because there's no
// assistant message in flight. These errors used to appear as a plain
// `toast({ description: err.message })` with no way to copy the full payload
// for a bug report.
//
// This helper:
//   1. Stringifies the error (instance, string, or unknown object)
//   2. Shows a destructive toast with the message body
//   3. Adds a "Copy" action button that puts the full diagnostic + URL on
//      the clipboard — same mental model as the ErrorLogBubble's Copy
//      button so users learn one affordance.
//
// Use anywhere a `try { ... } catch (err) { ... }` block currently calls
// `toast(...)` with an error message and a destructive variant.

import { toast } from '@/hooks/use-toast';
import { ToastAction } from '@/components/ui/toast';

function serializeError(err: unknown): { headline: string; payload: string } {
  if (err instanceof Error) {
    return {
      headline: err.message || err.name,
      payload: `${err.name}: ${err.message}\n\n${err.stack ?? '(no stack)'}`,
    };
  }
  if (typeof err === 'string') {
    return { headline: err, payload: err };
  }
  try {
    const json = JSON.stringify(err, null, 2);
    return { headline: 'Unknown error', payload: json };
  } catch {
    return { headline: 'Unknown error', payload: String(err) };
  }
}

export function copyableErrorToast(
  err: unknown,
  opts?: { title?: string; fallbackMessage?: string },
) {
  const { headline, payload } = serializeError(err);
  const description = headline || opts?.fallbackMessage || 'Unexpected error';
  const fullPayload = [
    opts?.title ?? 'Error',
    `URL: ${typeof window !== 'undefined' ? window.location.href : ''}`,
    '',
    payload,
  ].join('\n');

  toast({
    title: opts?.title ?? 'Error',
    description,
    variant: 'destructive',
    action: (
      <ToastAction
        altText="Copy error to clipboard"
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(fullPayload);
          } catch (e) {
            console.error('[copyableErrorToast] clipboard write failed', e);
          }
        }}
      >
        Copy
      </ToastAction>
    ),
  });
}
